import { memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Typography } from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { COLORS } from '../../constants';

const AUTO_DISMISS_MS = 4000;

/**
 * Reusable top banner popup for error / success feedback.
 * Pass `message` (and optional `title`) so the same component can be reused.
 * Auto-dismisses after a short delay; close button still works immediately.
 *
 * Rendered via portal to document.body so `position: fixed` stays viewport-
 * relative even when a parent (e.g. PagePanel) applies `transform`.
 */
const ErrorMessagePopup = memo(
  ({
    open = false,
    message = '',
    title = '',
    severity = 'error',
    autoHideDuration = AUTO_DISMISS_MS,
    onClose = () => {},
  }) => {
    useEffect(() => {
      if (!open || (!message && !title) || autoHideDuration <= 0) return undefined;

      const timerId = window.setTimeout(() => {
        onClose();
      }, autoHideDuration);

      return () => window.clearTimeout(timerId);
    }, [open, message, title, autoHideDuration, onClose]);

    if (!open || (!message && !title)) return null;

    const isSuccess = severity === 'success';
    const resolvedTitle = title || (isSuccess ? '' : 'Error Message');

    return createPortal(
      <Box
        role="alert"
        aria-live="assertive"
        sx={{
          position: 'fixed',
          // Clear AppBar (64px) + PagePanel tabs header so the banner is
          // never tucked under chrome, regardless of page scroll position.
          top: { xs: 152, md: 168 },
          left: '50%',
          transform: 'translateX(-50%)',
          // Above PagePanel content (1200) and sticky page headers (1100).
          zIndex: 2000,
          width: { xs: 'calc(100% - 32px)', sm: 'min(560px, calc(100% - 48px))' },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.75,
          borderRadius: '8px',
          backgroundColor: isSuccess ? '#ECFDF5' : COLORS.ERROR,
          color: isSuccess ? '#161E2D' : COLORS.WHITE,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
          borderTop: isSuccess ? `3px solid ${COLORS.SUCCESS}` : 'none',
        }}
      >
        {isSuccess ? (
          <CheckCircleIcon
            sx={{ fontSize: 28, flexShrink: 0, color: COLORS.SUCCESS }}
          />
        ) : (
          <BlockIcon sx={{ fontSize: 28, flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isSuccess ? (
            <Typography
              sx={{
                fontSize: '0.9375rem',
                lineHeight: 1.4,
                color: '#161E2D',
              }}
            >
              {resolvedTitle ? (
                <>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {resolvedTitle}
                  </Box>
                  {message ? (
                    <Box component="span" sx={{ fontWeight: 400 }}>
                      {' '}
                      {message}
                    </Box>
                  ) : null}
                </>
              ) : (
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {message}
                </Box>
              )}
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  lineHeight: 1.3,
                  mb: 0.5,
                }}
              >
                {resolvedTitle}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '0.8125rem',
                  lineHeight: 1.45,
                  opacity: 0.95,
                }}
              >
                {message}
              </Typography>
            </>
          )}
        </Box>
        <IconButton
          aria-label={isSuccess ? 'Close success message' : 'Close error message'}
          onClick={onClose}
          size="small"
          sx={{
            color: isSuccess ? COLORS.SUCCESS : COLORS.WHITE,
            flexShrink: 0,
            '&:hover': {
              backgroundColor: isSuccess
                ? 'rgba(16, 185, 129, 0.08)'
                : 'rgba(255,255,255,0.12)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>,
      document.body
    );
  }
);

ErrorMessagePopup.displayName = 'ErrorMessagePopup';

export default ErrorMessagePopup;
